import { Link, useLocation } from 'react-router-dom';
import { Activity, Star, Newspaper, Zap, ArrowRight, Search, Target, Gift, User } from 'lucide-react';
import { useScoresPath } from './scoresPath';
import ScoresEmailCapture from './ScoresEmailCapture';

/**
 * SmartKick Live Scores — top header + bottom nav + the persistent
 * "Get AI Predictions" funnel banner that sits just above the nav.
 *
 * On web, "AI Predictions" navigates to the marketing landing page
 * (`/welcome`) which describes the full SmartKick offering and hosts
 * the free-trial / Pro / VIP signup. On native iOS, we fall back to
 * the in-app PredictionsPromoModal (`onPredictionsCtaClick`) so we
 * don't route users out of the app — Apple-safe.
 */

const navKeys = [
  { key: '', label: 'Matches', icon: Activity, testid: 'nav-matches' },
  { key: 'favorites', label: 'Favorites', icon: Star, testid: 'nav-favorites' },
  { key: 'news', label: 'News', icon: Newspaper, testid: 'nav-news' },
];

const isNativePlatform = () =>
  typeof window !== 'undefined'
  && window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform();

const ScoresLayout = ({ children, onPredictionsCtaClick, onSearchClick, isSubscriber = false }) => {
  const { pathname } = useLocation();
  const native = isNativePlatform();
  const homePath = useScoresPath('');
  const favPath = useScoresPath('/favorites');
  const newsPath = useScoresPath('/news');
  const offersPath = useScoresPath('/offers');
  const navItems = [
    { to: homePath, label: 'Matches', icon: Activity, testid: 'nav-matches' },
    { to: favPath, label: 'Favorites', icon: Star, testid: 'nav-favorites' },
    { to: newsPath, label: 'News', icon: Newspaper, testid: 'nav-news' },
    // Affiliate bookmaker "Free Offers" — hidden inside the native app to stay
    // clear of App Store Guideline 5.3 (gambling). Still shown on the website;
    // can be re-enabled in-app later once compliance is sorted.
    ...(native ? [] : [{ to: offersPath, label: 'Free Offers', icon: Gift, testid: 'nav-offers' }]),
  ];

  // On web we want the AI Predictions CTAs to take users to the
  // marketing landing page. On native iOS we stay in-app to avoid
  // Apple's IAP routing concerns — open the promo modal instead.
  const predictionsCtaProps = native
    ? { as: 'button', onClick: onPredictionsCtaClick }
    : { as: 'a', href: '/welcome' };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--sk-bg)]">
      {/* ===== Top Header ===== */}
      <header
        className="sticky top-0 z-40 bg-[var(--sk-bg)] border-b border-[var(--sk-border)]"
        data-testid="scores-header"
      >
        <div className="max-w-md mx-auto px-4 h-14 flex items-center justify-between">
          <Link to={homePath} className="flex items-center gap-2" data-testid="scores-logo">
            <div className="w-8 h-8 rounded-lg bg-[var(--sk-accent)] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="text-[15px] font-black tracking-tight">SmartKick</div>
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[var(--sk-accent)]">Live Scores</div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5">
            {onSearchClick && (
              <button
                onClick={onSearchClick}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400"
                aria-label="Search"
                data-testid="header-search-btn"
              >
                <Search className="w-4 h-4" />
              </button>
            )}
            {/* Account entry point — gives users (and App Review) a clear path
                to profile & account deletion, required by Guideline 5.1.1(v). */}
            <Link
              to="/app/profile"
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400"
              aria-label="Account"
              data-testid="header-account-btn"
            >
              <User className="w-4 h-4" />
            </Link>
            {isSubscriber ? (
              <Link
                to="/app"
                className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                data-testid="header-predictions-cta"
              >
                Predictions
              </Link>
            ) : native ? (
              <button
                onClick={onPredictionsCtaClick}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                data-testid="header-predictions-cta"
              >
                AI Predictions
              </button>
            ) : (
              <a
                href={predictionsCtaProps.href}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                data-testid="header-predictions-cta"
              >
                AI Predictions
              </a>
            )}
          </div>
        </div>
      </header>

      {/* ===== Page content ===== */}
      <main className="flex-1 max-w-md w-full mx-auto pb-40" data-testid="scores-main">
        {children}
      </main>

      {/* One-time, skippable email capture (lead-gen → weekly tips digest) */}
      {!isSubscriber && <ScoresEmailCapture />}

      {/* ===== Funnel Banner (above nav) =====
          Subscribers see a shortcut into the in-app AI Predictions home.
          Non-subscribers see the free-trial pitch (modal on native,
          /welcome on web). */}
      {isSubscriber ? (
        <Link
          to="/app"
          data-testid="funnel-banner-subscriber"
          className="fixed bottom-16 left-0 right-0 z-30 mx-auto max-w-md flex items-center justify-between gap-3 px-4 py-2.5 bg-gradient-to-r from-emerald-500/95 to-emerald-600/95 backdrop-blur-md border-t border-emerald-300/30 text-left active:scale-[0.99] transition-transform"
        >
          <SubscriberBannerInner />
        </Link>
      ) : native ? (
        <button
          onClick={onPredictionsCtaClick}
          data-testid="funnel-banner"
          className="fixed bottom-16 left-0 right-0 z-30 mx-auto max-w-md flex items-center justify-between gap-3 px-4 py-2.5 bg-gradient-to-r from-emerald-500/95 to-emerald-600/95 backdrop-blur-md border-t border-emerald-300/30 text-left active:scale-[0.99] transition-transform"
        >
          <FunnelBannerInner />
        </button>
      ) : (
        <a
          href="/welcome"
          data-testid="funnel-banner"
          className="fixed bottom-16 left-0 right-0 z-30 mx-auto max-w-md flex items-center justify-between gap-3 px-4 py-2.5 bg-gradient-to-r from-emerald-500/95 to-emerald-600/95 backdrop-blur-md border-t border-emerald-300/30 text-left active:scale-[0.99] transition-transform"
        >
          <FunnelBannerInner />
        </a>
      )}

      {/* ===== Bottom Tab Nav ===== */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-[var(--sk-bg)] border-t border-[var(--sk-border)]"
        data-testid="scores-bottom-nav"
      >
        <div className="max-w-md mx-auto flex">
          {navItems.map((item) => {
            const active = pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors"
              >
                <Icon
                  className={`w-5 h-5 ${active ? 'text-[var(--sk-accent)]' : 'text-zinc-500'}`}
                  strokeWidth={active ? 2.5 : 2}
                />
                <span className={`text-[10px] font-bold ${active ? 'text-[var(--sk-accent)]' : 'text-zinc-500'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

const FunnelBannerInner = () => (
  <>
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
        <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <div className="text-[12px] font-black text-white leading-tight truncate">
          Get AI Predictions FREE for 14 days
        </div>
        <div className="text-[10px] font-medium text-white/85 truncate">
          Daily picks · Bet builders · Cancel anytime
        </div>
      </div>
    </div>
    <ArrowRight className="shrink-0 w-5 h-5 text-white" />
  </>
);

const SubscriberBannerInner = () => (
  <>
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
        <Target className="w-4 h-4 text-white" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <div className="text-[12px] font-black text-white leading-tight truncate">
          Open AI Predictions
        </div>
        <div className="text-[10px] font-medium text-white/85 truncate">
          Today's picks · Daily 10 · Bet Builders
        </div>
      </div>
    </div>
    <ArrowRight className="shrink-0 w-5 h-5 text-white" />
  </>
);

export default ScoresLayout;
